import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { buildYearOptions } from "@/lib/appointmentYears";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Area, AreaChart
} from "recharts";
import { Download, Calendar, Building2, User, TrendingUp, TrendingDown, DollarSign, ArrowUpRight, ArrowDownRight, Minus, FileSpreadsheet } from "lucide-react";
import { SavedFiltersBar } from "@/components/SavedFiltersBar";

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const QUARTERS = ["T1 (Ene-Mar)", "T2 (Abr-Jun)", "T3 (Jul-Sep)", "T4 (Oct-Dic)"];

export default function VoucherReports() {
  const { axiosAuth } = useAuth();
  const [tab, setTab] = useState("reports");

  return (
    <div className="space-y-6" data-testid="voucher-reports">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase tracking-tight">Informes</h1>
          <p className="text-sm text-slate-500 mt-1">Analisis y comparativas de atenciones comerciales</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {[
          { key: "reports", label: "Informe" },
          { key: "comparison", label: "Comparativa" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-5 py-2.5 text-xs uppercase tracking-[0.12em] font-bold border-b-2 transition-colors ${tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}
            data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "reports" && <ReportsTab axiosAuth={axiosAuth} />}
      {tab === "comparison" && <ComparisonTab axiosAuth={axiosAuth} />}
    </div>
  );
}


// ═══════════════════════════════════════════════════
// STANDARD REPORTS TAB
// ═══════════════════════════════════════════════════

function ReportsTab({ axiosAuth }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [center, setCenter] = useState("");
  const [type, setType] = useState("");
  const [centers, setCenters] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ period });
      if (center) params.append("center", center);
      if (type) params.append("type", type);
      if (selectedYear) params.append("year", selectedYear.toString());
      const r = await axiosAuth().get(`/vouchers/reports?${params}`);
      setData(r.data);
    } catch { toast.error("Error al cargar informes"); }
    finally { setLoading(false); }
  }, [axiosAuth, period, center, type, selectedYear]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axiosAuth().get("/vouchers/config").then(r => setCenters(r.data.centers || [])).catch(() => {});
  }, [axiosAuth]);

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams({ period });
      if (center) params.append("center", center);
      if (type) params.append("type", type);
      if (selectedYear) params.append("year", selectedYear.toString());
      const r = await axiosAuth().get(`/vouchers/reports/export-excel?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `bonos_${selectedYear}_${period}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar"); }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({ period });
      if (center) params.append("center", center);
      if (type) params.append("type", type);
      if (selectedYear) params.append("year", selectedYear.toString());
      const r = await axiosAuth().get(`/vouchers/reports/export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `bonos_${selectedYear}_${period}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al exportar CSV"); }
  };

  if (loading || !data) return <div className="p-8 text-center text-sm text-slate-400">Cargando informes...</div>;

  const { summary, by_center, by_issuer, by_type } = data;

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2 flex-wrap">
          <Select value={selectedYear.toString()} onValueChange={v => { setSelectedYear(parseInt(v)); setLoading(true); }}>
            <SelectTrigger className="w-[90px] rounded-sm h-8 text-xs border-slate-200 font-mono font-semibold" data-testid="report-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {buildYearOptions().map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={v => { setPeriod(v); setLoading(true); }}>
            <SelectTrigger className="w-[130px] rounded-sm h-8 text-xs border-slate-200" data-testid="report-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Este mes</SelectItem>
              <SelectItem value="quarter">Trimestre</SelectItem>
              <SelectItem value="year">Año completo</SelectItem>
            </SelectContent>
          </Select>
          {centers.length > 0 && (
            <Select value={center || "all"} onValueChange={v => { setCenter(v === "all" ? "" : v); setLoading(true); }}>
              <SelectTrigger className="w-[140px] rounded-sm h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos centros</SelectItem>
                {centers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={type || "all"} onValueChange={v => { setType(v === "all" ? "" : v); setLoading(true); }}>
            <SelectTrigger className="w-[140px] rounded-sm h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos tipos</SelectItem>
              <SelectItem value="lavado">Lavado</SelectItem>
              <SelectItem value="cortesia">Cortesia</SelectItem>
              <SelectItem value="atencion_comercial">Atencion comercial</SelectItem>
              <SelectItem value="cheque_regalo">Cheque regalo</SelectItem>
              <SelectItem value="personalizado">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportExcel} className="rounded-sm text-[10px] uppercase h-8 px-4 tracking-wider font-semibold" data-testid="export-excel-btn">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> Excel
          </Button>
          <Button variant="ghost" size="sm" onClick={exportCsv} className="rounded-sm text-[10px] uppercase h-8 px-3 tracking-wider font-semibold text-slate-400">
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
        </div>
      </div>
      <SavedFiltersBar module="reports"
        currentFilters={{ selectedYear: selectedYear.toString(), period, center, type }}
        onLoadFilter={(f) => {
          if (f.selectedYear) setSelectedYear(parseInt(f.selectedYear));
          if (f.period) setPeriod(f.period);
          setCenter(f.center || "");
          setType(f.type || "");
          setLoading(true);
        }}
      />

      {/* Summary */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-5">
          <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4">
            Resumen | {data.period.from} — {data.period.to}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
            {[
              { label: "Emitidos", value: summary.total },
              { label: "Canjeados", value: summary.redeemed, color: "text-emerald-700" },
              { label: "Pendientes", value: summary.pending, color: "text-blue-700" },
              { label: "Caducados", value: summary.expired, color: "text-amber-700" },
              { label: "Anulados", value: summary.cancelled, color: "text-red-700" },
              { label: "Ratio canje", value: `${summary.redeem_ratio}%`, color: "text-violet-700" },
              { label: "Valor total", value: `${summary.total_value.toLocaleString("es-ES")}€`, color: "text-slate-900" },
            ].map(k => (
              <div key={k.label} className="text-center">
                <p className={`text-xl font-bold ${k.color || "text-slate-900"}`}>{k.value}</p>
                <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold mt-0.5">{k.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* By center, issuer, type */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Por centro</p>
            {by_center.length > 0 ? by_center.map(c => (
              <div key={c.center} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-xs">
                <span className="text-slate-700 font-medium">{c.center}</span>
                <div className="flex gap-3 text-[10px]">
                  <span><strong>{c.emitidos}</strong> emit.</span>
                  <span className="text-emerald-600"><strong>{c.canjeados}</strong> canj.</span>
                  <span className="font-mono">{c.valor}€</span>
                </div>
              </div>
            )) : <p className="text-xs text-slate-400 text-center py-4">Sin datos</p>}
          </CardContent>
        </Card>

        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Por emisor</p>
            {by_issuer.length > 0 ? by_issuer.map(iss => (
              <div key={iss.issuer} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-xs">
                <span className="text-slate-700 font-medium">{iss.issuer}</span>
                <div className="flex gap-3 text-[10px]">
                  <span><strong>{iss.emitidos}</strong> emit.</span>
                  <span className="font-mono">{iss.valor}€</span>
                </div>
              </div>
            )) : <p className="text-xs text-slate-400 text-center py-4">Sin datos</p>}
          </CardContent>
        </Card>

        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Por tipo</p>
            {by_type.length > 0 ? by_type.map(t => (
              <div key={t.type} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-xs">
                <span className="text-slate-700 font-medium">{t.name}</span>
                <div className="flex gap-3 text-[10px]">
                  <span><strong>{t.emitidos}</strong> emit.</span>
                  <span className="text-emerald-600"><strong>{t.canjeados}</strong> canj.</span>
                  <span className="font-mono">{t.valor}€</span>
                </div>
              </div>
            )) : <p className="text-xs text-slate-400 text-center py-4">Sin datos</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════
// COMPARISON TAB
// ═══════════════════════════════════════════════════

function ComparisonTab({ axiosAuth }) {
  const currentYear = new Date().getFullYear();
  const [year1, setYear1] = useState(currentYear);
  const [year2, setYear2] = useState(currentYear - 1);
  const [period, setPeriod] = useState("year");
  const [month, setMonth] = useState(null);
  const [quarter, setQuarter] = useState(null);
  const [center, setCenter] = useState("");
  const [type, setType] = useState("");
  const [centers, setCenters] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axiosAuth().get("/vouchers/config").then(r => setCenters(r.data.centers || [])).catch(() => {});
  }, [axiosAuth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year1: year1.toString(), year2: year2.toString(), period });
      if (period === "month" && month) params.append("month", month.toString());
      if (period === "quarter" && quarter) params.append("quarter", quarter.toString());
      if (center) params.append("center", center);
      if (type) params.append("type", type);
      const r = await axiosAuth().get(`/vouchers/reports/comparison?${params}`);
      setData(r.data);
    } catch { toast.error("Error al cargar comparativa"); }
    finally { setLoading(false); }
  }, [axiosAuth, year1, year2, period, month, quarter, center, type]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams({ year1: year1.toString(), year2: year2.toString(), period });
      if (period === "month" && month) params.append("month", month.toString());
      if (period === "quarter" && quarter) params.append("quarter", quarter.toString());
      if (center) params.append("center", center);
      if (type) params.append("type", type);
      const r = await axiosAuth().get(`/vouchers/reports/comparison/export-excel?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `comparativa_${year1}_vs_${year2}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar"); }
  };

  const VariationBadge = ({ value, suffix = "%", isRatio = false }) => {
    const num = parseFloat(value);
    if (num === 0) return <span className="flex items-center gap-0.5 text-[10px] text-slate-400 font-medium"><Minus className="w-3 h-3" /> 0{suffix}</span>;
    const positive = num > 0;
    const Icon = positive ? ArrowUpRight : ArrowDownRight;
    const color = positive ? "text-emerald-600" : "text-red-600";
    const bg = positive ? "bg-emerald-50" : "bg-red-50";
    return (
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${color} ${bg}`}>
        <Icon className="w-3 h-3" />
        {positive ? "+" : ""}{isRatio ? num.toFixed(1) + " pts" : num.toFixed(1) + suffix}
      </span>
    );
  };

  const yearRange = Array.from({ length: 7 }, (_, i) => currentYear - 4 + i);

  return (
    <div className="space-y-5">
      {/* Filters */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold mb-1">Año principal</p>
              <Select value={year1.toString()} onValueChange={v => setYear1(parseInt(v))}>
                <SelectTrigger className="w-[90px] rounded-sm h-8 text-xs font-mono font-semibold border-slate-900" data-testid="comp-year1"><SelectValue /></SelectTrigger>
                <SelectContent>{yearRange.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-400 font-bold pb-1.5">vs</p>
            <div>
              <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold mb-1">Año comparado</p>
              <Select value={year2.toString()} onValueChange={v => setYear2(parseInt(v))}>
                <SelectTrigger className="w-[90px] rounded-sm h-8 text-xs font-mono font-semibold border-slate-300" data-testid="comp-year2"><SelectValue /></SelectTrigger>
                <SelectContent>{yearRange.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold mb-1">Periodo</p>
              <Select value={period} onValueChange={v => { setPeriod(v); if (v === "month" && !month) setMonth(1); if (v === "quarter" && !quarter) setQuarter(1); }}>
                <SelectTrigger className="w-[120px] rounded-sm h-8 text-xs border-slate-200" data-testid="comp-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="year">Anual</SelectItem>
                  <SelectItem value="quarter">Trimestral</SelectItem>
                  <SelectItem value="month">Mensual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {period === "month" && (
              <div>
                <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold mb-1">Mes</p>
                <Select value={(month || 1).toString()} onValueChange={v => setMonth(parseInt(v))}>
                  <SelectTrigger className="w-[100px] rounded-sm h-8 text-xs border-slate-200" data-testid="comp-month"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {period === "quarter" && (
              <div>
                <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold mb-1">Trimestre</p>
                <Select value={(quarter || 1).toString()} onValueChange={v => setQuarter(parseInt(v))}>
                  <SelectTrigger className="w-[140px] rounded-sm h-8 text-xs border-slate-200" data-testid="comp-quarter"><SelectValue /></SelectTrigger>
                  <SelectContent>{QUARTERS.map((q, i) => <SelectItem key={i} value={(i + 1).toString()}>{q}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {centers.length > 0 && (
              <div>
                <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold mb-1">Centro</p>
                <Select value={center || "all"} onValueChange={v => setCenter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[140px] rounded-sm h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {centers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold mb-1">Tipo</p>
              <Select value={type || "all"} onValueChange={v => setType(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[130px] rounded-sm h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="lavado">Lavado</SelectItem>
                  <SelectItem value="cortesia">Cortesia</SelectItem>
                  <SelectItem value="atencion_comercial">Atencion comercial</SelectItem>
                  <SelectItem value="cheque_regalo">Cheque regalo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto">
              <Button variant="outline" size="sm" onClick={exportExcel} className="rounded-sm text-[10px] uppercase h-8 px-4 tracking-wider font-semibold" data-testid="export-comp-excel">
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> Excel
              </Button>
            </div>
          </div>
          <div className="pt-2 border-t border-slate-100 mt-3">
            <SavedFiltersBar module="comparison"
              currentFilters={{ year1: year1.toString(), year2: year2.toString(), period, month: month?.toString() || "", quarter: quarter?.toString() || "", center, type }}
              onLoadFilter={(f) => {
                if (f.year1) setYear1(parseInt(f.year1));
                if (f.year2) setYear2(parseInt(f.year2));
                if (f.period) setPeriod(f.period);
                setMonth(f.month ? parseInt(f.month) : null);
                setQuarter(f.quarter ? parseInt(f.quarter) : null);
                setCenter(f.center || "");
                setType(f.type || "");
              }}
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="p-8 text-center text-sm text-slate-400">Cargando comparativa...</div>
      ) : data ? (
        <>
          {/* KPI comparison cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="comparison-kpis">
            {[
              { key: "emitidos", label: "Emitidos", icon: TrendingUp },
              { key: "canjeados", label: "Canjeados", icon: TrendingUp },
              { key: "pendientes", label: "Pendientes", icon: TrendingUp },
              { key: "caducados", label: "Caducados", icon: TrendingDown },
              { key: "anulados", label: "Anulados", icon: TrendingDown },
              { key: "ratio_canje", label: "Ratio canje", icon: TrendingUp, isRatio: true, suffix: "%" },
              { key: "valor_total", label: "Valor total", icon: DollarSign, suffix: "€" },
            ].map(k => {
              const comp = data.comparison[k.key];
              return (
                <Card key={k.key} className="border-slate-200 rounded-sm">
                  <CardContent className="p-4">
                    <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold mb-2">{k.label}</p>
                    <div className="flex items-end gap-2 mb-1.5">
                      <span className="text-lg font-bold text-slate-900">
                        {k.suffix === "€" ? `${comp.y1.toLocaleString("es-ES")}€` : k.isRatio ? `${comp.y1}%` : comp.y1}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-400">
                        {year2}: {k.suffix === "€" ? `${comp.y2.toLocaleString("es-ES")}€` : k.isRatio ? `${comp.y2}%` : comp.y2}
                      </span>
                      <VariationBadge value={comp.pct} suffix={k.isRatio ? "" : "%"} isRatio={k.isRatio} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Monthly TREND chart — Lines for clear year-over-year comparison */}
          <Card className="border-slate-200 rounded-sm">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-1">
                Tendencia de emision — {year1} vs {year2}
              </p>
              <p className="text-[9px] text-slate-400 mb-4">Lineas superpuestas para comparar la evolucion mensual</p>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.monthly_chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4, border: "1px solid #e2e8f0" }} />
                  <Legend iconType="plainline" wrapperStyle={{ fontSize: 10 }} />
                  <Line type="monotone" dataKey={`emitidos_${year1}`} stroke="#0f172a" strokeWidth={2.5} dot={{ r: 3, fill: "#0f172a" }} name={`Emitidos ${year1}`} />
                  <Line type="monotone" dataKey={`emitidos_${year2}`} stroke="#94a3b8" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2.5, fill: "#94a3b8" }} name={`Emitidos ${year2}`} />
                  <Line type="monotone" dataKey={`canjeados_${year1}`} stroke="#059669" strokeWidth={2.5} dot={{ r: 3, fill: "#059669" }} name={`Canjeados ${year1}`} />
                  <Line type="monotone" dataKey={`canjeados_${year2}`} stroke="#a7f3d0" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2.5, fill: "#a7f3d0" }} name={`Canjeados ${year2}`} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Valor evolution — Area chart */}
          <Card className="border-slate-200 rounded-sm">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-1">
                Evolucion del valor — {year1} vs {year2}
              </p>
              <p className="text-[9px] text-slate-400 mb-4">Valor acumulado mensual en EUR</p>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data.monthly_chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={v => `${v}€`} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4, border: "1px solid #e2e8f0" }} formatter={(v) => [`${v}€`]} />
                  <Legend iconType="plainline" wrapperStyle={{ fontSize: 10 }} />
                  <Area type="monotone" dataKey={`valor_${year1}`} stroke="#0f172a" fill="#0f172a" fillOpacity={0.08} strokeWidth={2} name={`Valor ${year1}`} />
                  <Area type="monotone" dataKey={`valor_${year2}`} stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.04} strokeWidth={1.5} strokeDasharray="5 3" name={`Valor ${year2}`} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Monthly bar chart — Volume comparison */}
          <Card className="border-slate-200 rounded-sm">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4">
                Volumen mensual — Emitidos y canjeados
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.monthly_chart} barGap={1} barCategoryGap="15%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4, border: "1px solid #e2e8f0" }} />
                  <Legend iconType="square" wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey={`emitidos_${year1}`} fill="#0f172a" radius={[2, 2, 0, 0]} name={`Emitidos ${year1}`} />
                  <Bar dataKey={`emitidos_${year2}`} fill="#cbd5e1" radius={[2, 2, 0, 0]} name={`Emitidos ${year2}`} />
                  <Bar dataKey={`canjeados_${year1}`} fill="#059669" radius={[2, 2, 0, 0]} name={`Canjeados ${year1}`} />
                  <Bar dataKey={`canjeados_${year2}`} fill="#a7f3d0" radius={[2, 2, 0, 0]} name={`Canjeados ${year2}`} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Comparison table */}
          <Card className="border-slate-200 rounded-sm">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4">
                Tabla comparativa — {year1} vs {year2}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="comparison-table">
                  <thead>
                    <tr className="bg-slate-900 text-white">
                      <th className="text-left px-4 py-2.5 font-bold text-[10px] uppercase tracking-[0.12em]">KPI</th>
                      <th className="text-center px-4 py-2.5 font-bold text-[10px] uppercase tracking-[0.12em]">{year1}</th>
                      <th className="text-center px-4 py-2.5 font-bold text-[10px] uppercase tracking-[0.12em]">{year2}</th>
                      <th className="text-center px-4 py-2.5 font-bold text-[10px] uppercase tracking-[0.12em]">Variacion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "emitidos", label: "Emitidos" },
                      { key: "canjeados", label: "Canjeados" },
                      { key: "pendientes", label: "Pendientes" },
                      { key: "caducados", label: "Caducados" },
                      { key: "anulados", label: "Anulados" },
                      { key: "ratio_canje", label: "Ratio de canje", isRatio: true },
                      { key: "valor_total", label: "Valor total (EUR)", isValue: true },
                    ].map((r, i) => {
                      const comp = data.comparison[r.key];
                      const fmt = (v) => r.isValue ? `${v.toLocaleString("es-ES")}€` : r.isRatio ? `${v}%` : v;
                      return (
                        <tr key={r.key} className={i % 2 === 0 ? "bg-slate-50/60" : ""}>
                          <td className="px-4 py-2.5 font-semibold text-slate-700">{r.label}</td>
                          <td className="text-center px-4 py-2.5 font-bold text-slate-900">{fmt(comp.y1)}</td>
                          <td className="text-center px-4 py-2.5 text-slate-500">{fmt(comp.y2)}</td>
                          <td className="text-center px-4 py-2.5">
                            <VariationBadge value={comp.pct} suffix={r.isRatio ? "" : "%"} isRatio={r.isRatio} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Monthly detail table */}
          <Card className="border-slate-200 rounded-sm">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4">
                Detalle mensual comparado
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-900 text-white">
                      <th className="text-left px-3 py-2 font-bold text-[10px] uppercase tracking-[0.1em]">Mes</th>
                      <th className="text-center px-3 py-2 font-bold text-[10px]">Emit. {year1}</th>
                      <th className="text-center px-3 py-2 font-bold text-[10px]">Emit. {year2}</th>
                      <th className="text-center px-3 py-2 font-bold text-[10px]">Canj. {year1}</th>
                      <th className="text-center px-3 py-2 font-bold text-[10px]">Canj. {year2}</th>
                      <th className="text-center px-3 py-2 font-bold text-[10px]">Valor {year1}</th>
                      <th className="text-center px-3 py-2 font-bold text-[10px]">Valor {year2}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.monthly_chart.map((m, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-slate-50/60" : ""}>
                        <td className="px-3 py-2 font-bold text-slate-700">{m.label}</td>
                        <td className="text-center px-3 py-2 font-bold text-slate-900">{m[`emitidos_${year1}`]}</td>
                        <td className="text-center px-3 py-2 text-slate-400">{m[`emitidos_${year2}`]}</td>
                        <td className="text-center px-3 py-2 font-bold text-emerald-700">{m[`canjeados_${year1}`]}</td>
                        <td className="text-center px-3 py-2 text-emerald-400">{m[`canjeados_${year2}`]}</td>
                        <td className="text-center px-3 py-2 font-mono">{m[`valor_${year1}`]}€</td>
                        <td className="text-center px-3 py-2 font-mono text-slate-400">{m[`valor_${year2}`]}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
